import { motion } from "framer-motion";
import { ArrowRight, HandCoins, PieChart, ShieldCheck } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1494526585095-c41746248156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjBob21lJTIwZXh0ZXJpb3IlMjBzdW5zZXR8ZW58MHx8fHwxNzkxOTg1ODEwfDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-[#FFD3C6]/50 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-2 bg-[#FFD3C6]/60 text-[#C6373C] text-xs sm:text-sm font-bold tracking-wide px-4 py-2 rounded-full mb-6">
            <HandCoins className="w-4 h-4" />
            New Landlord Partnership Program
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-[#222222]">
            We Pay You <span className="text-[#FF5A5F]">$500</span> to Partner With Us!
          </h1>
          <p className="mt-4 font-heading text-xl sm:text-2xl font-bold text-[#222]">
            Your Property. Our Management. Your Profit.
          </p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#5a5a5a] max-w-xl">
            We help landlords turn their properties into professionally managed short-term rentals
            without the stress of handling bookings, guests, cleanings, or daily operations.
          </p>
          <p className="mt-3 text-base sm:text-lg font-bold text-[#222]">
            You keep <span className="text-[#FF5A5F]">80% of the monthly profits</span>. We handle the management.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="/#contact"
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-8 py-4 rounded-full text-base transition-all duration-200 shadow-[0_8px_24px_rgba(255,90,95,0.35)] hover:-translate-y-0.5"
            >
              See If My Property Qualifies
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="/#calculator"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#EBEBEB] hover:border-[#FF5A5F] text-[#222] font-bold px-8 py-4 rounded-full text-base transition-all duration-200 hover:-translate-y-0.5"
            >
              Get My Free Income Estimate
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold text-[#555]">
            <span className="inline-flex items-center gap-2" data-testid="hero-trust-incentive">
              <HandCoins className="w-4 h-4 text-[#FF5A5F]" /> $500 paid to qualifying landlords
            </span>
            <span className="inline-flex items-center gap-2" data-testid="hero-trust-split">
              <PieChart className="w-4 h-4 text-[#FF5A5F]" /> 80/20 profit sharing
            </span>
            <span className="inline-flex items-center gap-2" data-testid="hero-trust-control">
              <ShieldCheck className="w-4 h-4 text-[#FF5A5F]" /> No lease required
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.14)]">
            <img
              src={HERO_IMAGE}
              alt="Beautifully furnished home earning short-term rental income through the Shonta Solutions landlord partnership program"
              className="w-full h-[320px] sm:h-[440px] lg:h-[520px] object-cover"
              data-testid="hero-image"
            />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] px-6 py-4 border border-[#EBEBEB]">
            <p className="text-xs font-bold tracking-widest text-[#FF5A5F] uppercase">$500 Paid to You</p>
            <p className="text-sm font-semibold text-[#333] mt-1">For qualifying partnership properties</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
